import {
  TCutbankInTransactionLogPage,
  TRESPONSE_CUTBANK_IN_TRANSACTION_LOG,
  TRESPONSE_CUTBANK_OUT_TRANSACTION_LOG,
} from '@monorepo/types';
import { TTableOptions, TTableTreeModel } from '@monorepo/ui';
import moment from 'moment';
import { ModifiersZTableModel } from '../../../table.modifier';
import { TActionCellType, TTableCellTree, TTextCell } from '../../../table.type';

const getValue = (
  header: TCutbankInTransactionLogPage['table_config']['table_main_headers'][number],
  item: TRESPONSE_CUTBANK_IN_TRANSACTION_LOG['logs'][number],
): string | number => {
  if (header.key === 'datetime') {
    return moment(item.datetime).format('DD MMMM') + ', ' + moment(item.datetime).format('hh:mm A');
  } else if (header.key === 'actual_qty') {
    return item.item.actual_qty;
  } else if (header.key === 'tagQty') {
    return (item.item.details[header.key] || item.item.actual_qty) as string | number;
  } else {
    return item.item.details[header.key] as string | number;
  }
};

const getFooterConfig = (
  tableConfig: TCutbankInTransactionLogPage['table_config'],
  totalQty: number,
  totalCount: number,
  isRfIdMappingEnabled: boolean,
) => {
  const footerList = [
    ...tableConfig.table_main_headers,
    ...tableConfig.collapsible_list_config.table_in_row_column,
    'action-cell',
    ...(isRfIdMappingEnabled ? ['action-cell', 'action-cell'] : []),
  ];

  const footerCols: { type: 'TEXT_CELL'; text: string | number }[] = footerList.map((col, index) => {
    if (index === 0) {
      return {
        type: 'TEXT_CELL',
        text: `TOTAL BUNDLES ${totalCount}`,
        align: 'left',
      };
    } else if (
      isRfIdMappingEnabled
        ? index === tableConfig.table_main_headers.length - 2
        : index === tableConfig.table_main_headers.length - 1
    ) {
      return {
        type: 'TEXT_CELL',
        text: `TOTAL QTY ${totalQty}`,
        align: 'left',
      };
    } else {
      return {
        type: 'TEXT_CELL',
        text: '',
      };
    }
  });

  return footerCols;
};

const getHeaderConfig = (
  tableConfig: TCutbankInTransactionLogPage['table_config'],
  isRfIdMappingEnabled: boolean,
  isTabletScreen = false,
) => {
  const mainHeaders = tableConfig.table_main_headers.map((head, index) => {
    return {
      type: 'TEXT_CELL',
      text: head.display_name,
      align: 'left',
      ...(!isTabletScreen && {
        stickyType: 'LEFT',
        stickyPosition: index + 1,
      }),
      widthInPx: 120,
      maxWidthInPx: 120,
      ...(index === tableConfig.table_main_headers.length - 1 && { borderRight: true }),
    };
  });

  const secondaryHeaders = tableConfig.collapsible_list_config.table_in_row_column.map((head) => {
    return {
      type: 'TEXT_CELL',
      text: head.display_name,
      align: 'left',
    };
  });

  const additionalActionCells = [
    {
      type: 'TEXT_CELL',
      text: 'cutbank_out_rf_id_scan.status',
      align: 'left',
      widthInPx: 60,
      stickyType: 'RIGHT',
    },
    {
      type: 'TEXT_CELL',
      text: '',
      align: 'left',
      widthInPx: 100,
      stickyType: 'RIGHT',
    },
  ];

  const actionCells = [
    ...(isRfIdMappingEnabled ? additionalActionCells : []),
    {
      type: 'TEXT_CELL',
      text: '',
      align: 'left',
      widthInPx: 40,
      stickyType: 'RIGHT',
    },
  ];

  return [...mainHeaders, ...secondaryHeaders, ...actionCells];
};

const getRows = (
  item: TRESPONSE_CUTBANK_IN_TRANSACTION_LOG['logs'][number],
  tableConfig: TCutbankInTransactionLogPage['table_config'],
  isRfIdMappingEnabled: boolean,
) => {
  const mainRows = tableConfig.table_main_headers.map((head) => {
    const value = getValue(head, item);
    return {
      type: 'TEXT_CELL',
      text: value,
      align: 'left',
    };
  });

  const secondaryRows = tableConfig.collapsible_list_config.table_in_row_column.map((head) => {
    const value = getValue(head, item);
    return {
      type: 'TEXT_CELL',
      text: value,
      align: 'left',
    };
  });

  const additionalActioncells: (TActionCellType | TTextCell)[] = [
    {
      type: 'TEXT_CELL',
      text: item.rfidMappingStatus ? 'cutbank_out_rf_id_scan.COMPLETED' : 'cutbank_out_rf_id_scan.pending',
      align: 'left',
      textColor: item.rfidMappingStatus ? 'GREEN' : 'BLACK-LIGHT-BOLD',
    },
    {
      type: 'ACTION_CELL',
      actionType: 'RF_ID_MAPPING',
      asIcon: false,
      actionName: 'cutbank_out_rf_id_scan.rdIdMapping',
    },
  ];

  const actionCells: (TActionCellType | TTextCell)[] = [
    ...(isRfIdMappingEnabled ? additionalActioncells : []),
    {
      type: 'ACTION_CELL',
      actionType: 'UNDO',
      asIcon: true,
    },
  ];

  return [...mainRows, ...secondaryRows, ...actionCells];
};

export const CUTBANK_TRANSACTION_LOG_TABLE = {
  createModel: (
    items: TRESPONSE_CUTBANK_IN_TRANSACTION_LOG['logs'] | TRESPONSE_CUTBANK_OUT_TRANSACTION_LOG['logs'],
    tableConfig: TCutbankInTransactionLogPage['table_config'],
    totalCount: number,
    totalQty: number,
    isRfIdMappingEnabled: boolean,
    isTabletScreen: boolean = false,
  ) => {
    return ModifiersZTableModel.createTableModel(
      items,
      {
        headerConfig: getHeaderConfig(
          tableConfig,
          isRfIdMappingEnabled,
          isTabletScreen,
        ) as TTableOptions['headerConfig'],
        footerConfig: getFooterConfig(tableConfig, totalQty, totalCount, isRfIdMappingEnabled),
      },
      (item) => {
        return {
          type: 'ROW',
          rowItem: item,
          cells: getRows(item, tableConfig, isRfIdMappingEnabled) as TTableCellTree[],
        };
      },
    );
  },

  emptyModel: (
    items: TRESPONSE_CUTBANK_IN_TRANSACTION_LOG['logs'],
    tableConfig: TCutbankInTransactionLogPage['table_config'],
  ) => {
    return ModifiersZTableModel.createTableModel(
      items,
      {
        headerConfig: tableConfig.table_main_headers.map((head, index) => {
          return {
            type: 'TEXT_CELL' as const,
            text: head.display_name,
            align: 'left' as const,
          };
        }),
      },
      (item) => {
        return {
          type: 'ROW' as const,
          rowItem: item,
          cells: tableConfig.table_main_headers.map((head) => {
            const value = getValue(head, item);
            return {
              type: 'TEXT_CELL' as const,
              text: value,
              align: 'left' as const,
            };
          }),
        };
      },
    );
  },
};

export type TCutbankInTransactionLogTableType = TTableTreeModel<TRESPONSE_CUTBANK_IN_TRANSACTION_LOG['logs'][number]>;
